import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Inline banner shown to any logged-in user whose latest signed waiver is on an
 * older version than the current one. Soft-prompts a re-sign before they hit
 * the lane. We don't *block* the entire app (Terms gate already covers compliance);
 * we just nudge prominently and link to /checkin.
 *
 * Hidden on /checkin itself, /login, /terms, /privacy, and admin pages so the
 * banner doesn't crowd those screens.
 */
const WaiverResignBanner = () => {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    if (loading || !user) return;
    api.get("/me/waiver-status", { withCredentials: true })
      .then((r) => { if (active) setStatus(r.data); })
      .catch(() => { if (active) setStatus(null); });
    return () => { active = false; };
  }, [loading, user]);

  if (loading || !user || !status?.needs_resign) return null;

  // Routes where we hide the banner to avoid crowding the UX
  const hideOn = ["/checkin", "/login", "/terms", "/privacy", "/auth/google"];
  if (hideOn.some((p) => location.pathname.startsWith(p))) return null;
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <div
      className="bg-amber-500/10 border-y border-amber-500/30 text-amber-100"
      data-testid="waiver-resign-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0" />
        <div className="text-sm font-exo flex-1 min-w-0">
          <strong className="text-amber-200">Liability waiver updated.</strong>{" "}
          <span className="text-amber-100/80">
            You signed version <code className="text-amber-200">{status.version}</code>; we&apos;re now on{" "}
            <code className="text-amber-200">{status.current_version}</code>. Please re-sign before your next visit.
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/checkin")}
          data-testid="waiver-resign-cta"
          className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-black font-orbitron text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-sm transition-colors"
        >
          Re-sign now
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default WaiverResignBanner;
